// src/pages/shared/SecurityPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { userService } from '../../services/userService';
import { logout, updateUser, updateUserPreferences } from '../../store/slices/authSlice';
import { useUI } from '../../context/UIContext';
import { 
  ArrowBackIcon, EyeIcon, EyeOffIcon, DeleteIcon
} from '../../components/common/Icons';
import TeslaButton from '../../components/common/TeslaButton';
import '../../styles/SecurityPage.css';

// Import centralized password validation
import { isPasswordValid, getPasswordErrorMessage } from '../../utils/passwordValidator';
import PasswordRequirements from '../../components/common/PasswordRequirements';

// Password requirements are now in centralized utils/passwordValidator.ts

interface SecuritySettings {
  biometricEnabled: boolean;
  twoFactorEnabled: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
}

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type NotificationChannelValue = boolean | { type?: boolean } | undefined;

const getNotificationEnabled = (value: NotificationChannelValue, defaultValue = true): boolean => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'object' && value.type !== undefined) return Boolean(value.type);
  return defaultValue;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// apiClient rejects with { status, message, data } rather than an AxiosError.
const getApiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const { setLoading: setUILoading, showSnackbar, showConfirmDialog } = useUI();

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    biometricEnabled: false,
    twoFactorEnabled: false,
    emailNotifications: true,
    pushNotifications: true,
  });

  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [loading, setLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [showExportPassword, setShowExportPassword] = useState(false);
  const deletionActionInFlight = useRef(false);
  const [scheduledDeletionDate, setScheduledDeletionDate] = useState<string | null>(
    user?.deletionRequested && user?.scheduledDeletionDate
      ? String(user.scheduledDeletionDate)
      : null
  );

  useEffect(() => {
    if (user?.deletionRequested && user?.scheduledDeletionDate) {
      setScheduledDeletionDate(String(user.scheduledDeletionDate));
    } else if (!user?.deletionRequested) {
      setScheduledDeletionDate(null);
    }
  }, [user?.deletionRequested, user?.scheduledDeletionDate]);

  useEffect(() => {
    const notifications = user?.preferences?.notifications;
    if (!notifications) return;

    setSecuritySettings(prev => ({
      ...prev,
      emailNotifications: getNotificationEnabled(notifications.email),
      pushNotifications: getNotificationEnabled(notifications.push),
    }));
  }, [user?.preferences?.notifications]);

  const handleToggleSetting = async (setting: keyof SecuritySettings) => {
    if (setting !== 'emailNotifications' && setting !== 'pushNotifications') {
      return;
    }

    const channel = setting === 'emailNotifications' ? 'email' : 'push';
    const newValue = !securitySettings[setting];
    const existingNotifications = user?.preferences?.notifications || {};

    try {
      setLoading(true);
      setUILoading(true);
      
      const response = await userService.updateSecuritySettings({
        emailNotifications: channel === 'email'
          ? newValue
          : getNotificationEnabled(existingNotifications.email),
        pushNotifications: channel === 'push'
          ? newValue
          : getNotificationEnabled(existingNotifications.push),
      });

      if (response?.success !== false) {
        setSecuritySettings(prev => ({
          ...prev,
          [setting]: newValue,
        }));
        if (response?.data) {
          dispatch(updateUserPreferences(response.data));
        }
        const label = channel === 'email' ? 'Email notifications' : 'Push notifications';
        showSnackbar(`${label} ${newValue ? 'enabled' : 'disabled'}`, 'success');
      }
    } catch (error: any) {
      console.error('Error updating security settings:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to update notification settings',
        'error'
      );
    } finally {
      setLoading(false);
      setUILoading(false);
    }
  };

  const validatePasswordChange = (): boolean => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword) {
      showSnackbar('Current password is required', 'error');
      return false;
    }
    if (!newPassword) {
      showSnackbar('New password is required', 'error');
      return false;
    }
    if (newPassword !== confirmPassword) {
      showSnackbar('Passwords do not match', 'error');
      return false;
    }
    
    // Use centralized password validation
    if (!isPasswordValid(newPassword)) {
      const errorMessage = getPasswordErrorMessage(newPassword);
      showSnackbar(errorMessage || 'Password does not meet requirements', 'error');
      return false;
    }
    
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordChange()) return;

    try {
      setLoading(true);
      setUILoading(true);
      
      const response = await userService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response?.success !== false) {
        showSnackbar('Password updated successfully', 'success');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        
        setLoading(false);
        setUILoading(false);
        
        showConfirmDialog(
          'Password Updated',
          'For security reasons, you will need to log in again with your new password.',
          () => {
            dispatch(logout());
            navigate('/login');
          }
        );
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update password', 'error');
    } finally {
      setLoading(false);
      setUILoading(false);
    }
  };

  const handleExportAccountData = async () => {
    if (!exportPassword.trim()) {
      showSnackbar('Enter your password to export your data', 'error');
      return;
    }

    try {
      setLoading(true);
      setUILoading(true);
      const blob = await userService.exportAccountData(exportPassword);
      const fileBlob =
        blob instanceof Blob
          ? blob
          : new Blob([blob], { type: 'application/json' });
      downloadBlob(fileBlob, `lookbel-data-export-${Date.now()}.json`);
      setExportPassword('');
      showSnackbar('Your data export has been downloaded', 'success');
    } catch (error: any) {
      console.error('Error exporting account data:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Failed to export account data',
        'error'
      );
    } finally {
      setLoading(false);
      setUILoading(false);
    }
  };

  const handleDeleteAccount = () => {
    if (deletionActionInFlight.current) return;
    if (!deletePassword.trim()) {
      showSnackbar('Enter your password to request account deletion', 'error');
      return;
    }

    showConfirmDialog(
      'Delete Account',
      'This records a deletion request. It does not delete your account today. You can cancel for 30 days from this page. Details: lookbel.com/delete-account.',
      async () => {
        if (deletionActionInFlight.current) return;
        deletionActionInFlight.current = true;
        try {
          setLoading(true);
          setUILoading(true);

          const response = await userService.deleteAccount(
            deletePassword,
            'User requested deletion via web security settings'
          );

          if (response?.success !== false) {
            const scheduled =
              response?.data?.scheduledDeletionDate ||
              new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
            setScheduledDeletionDate(String(scheduled));
            setDeletePassword('');
            dispatch(
              updateUser({
                deletionRequested: true,
                scheduledDeletionDate: String(scheduled),
              })
            );
            showSnackbar(
              'Deletion request recorded. Your account has not been deleted.',
              'success'
            );
          }
        } catch (error: any) {
          console.error('Error deleting account:', error);
          showSnackbar(
            getApiErrorMessage(error, 'Failed to request account deletion. Please try again.'),
            'error'
          );
        } finally {
          deletionActionInFlight.current = false;
          setLoading(false);
          setUILoading(false);
        }
      },
      undefined,
      { severity: 'error', confirmText: 'Request Deletion', cancelText: 'Cancel' }
    );
  };

  const handleCancelDeletion = () => {
    if (deletionActionInFlight.current) return;
    showConfirmDialog(
      'Cancel Account Deletion',
      'Keep your account and withdraw the deletion request?',
      async () => {
        if (deletionActionInFlight.current) return;
        deletionActionInFlight.current = true;
        try {
          setLoading(true);
          setUILoading(true);
          const response = await userService.cancelAccountDeletion();
          if (response?.success !== false) {
            setScheduledDeletionDate(null);
            dispatch(
              updateUser({
                deletionRequested: false,
                scheduledDeletionDate: undefined,
                deletionRequestedAt: undefined,
              })
            );
            showSnackbar('Account deletion cancelled', 'success');
          }
        } catch (error: any) {
          console.error('Error cancelling account deletion:', error);
          showSnackbar(
            getApiErrorMessage(error, 'Failed to cancel account deletion. Please try again.'),
            'error'
          );
        } finally {
          deletionActionInFlight.current = false;
          setLoading(false);
          setUILoading(false);
        }
      },
      undefined,
      { confirmText: 'Keep My Account', cancelText: 'Go Back' }
    );
  };

  const deletionDateLabel = scheduledDeletionDate
    ? new Date(scheduledDeletionDate).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="security-page">
      <div className="security-header">
        <button className="security-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className="security-title">Security Settings</h1>
      </div>

      <div className="security-content">
        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Change Password</h2>
          </div>
          <div className="tesla-card__content">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword.current ? 'text' : 'password'}
                  id="currentPassword"
                  className="tesla-input"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                >
                  {showPassword.current ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword.new ? 'text' : 'password'}
                  id="newPassword"
                  className="tesla-input"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                >
                  {showPassword.new ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {/* Password Strength Indicator - Using centralized component */}
              {passwordData.newPassword && (
                <PasswordRequirements password={passwordData.newPassword} />
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  className="tesla-input"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                >
                  {showPassword.confirm ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <TeslaButton
              variant="primary"
              onClick={handleChangePassword}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </TeslaButton>
          </div>
        </div>

        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Security Settings</h2>
          </div>
          <div className="tesla-card__content">
            <div className="security-setting-item">
              <div>
                <div className="security-setting-title">Biometric Login</div>
                <div className="security-setting-description">
                  Use Face ID or Touch ID to log in
                </div>
              </div>
              <label className="tesla-switch">
                <input
                  type="checkbox"
                  checked={securitySettings.biometricEnabled}
                  onChange={() => handleToggleSetting('biometricEnabled')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="security-setting-item">
              <div>
                <div className="security-setting-title">Two-Factor Authentication</div>
                <div className="security-setting-description">
                  Add an extra layer of security
                </div>
              </div>
              <label className="tesla-switch">
                <input
                  type="checkbox"
                  checked={securitySettings.twoFactorEnabled}
                  onChange={() => handleToggleSetting('twoFactorEnabled')}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Notifications</h2>
          </div>
          <div className="tesla-card__content">
            <div className="security-setting-item">
              <div>
                <div className="security-setting-title">Email Notifications</div>
                <div className="security-setting-description">
                  Receive security alerts via email
                </div>
              </div>
              <label className="tesla-switch">
                <input
                  type="checkbox"
                  checked={securitySettings.emailNotifications}
                  onChange={() => handleToggleSetting('emailNotifications')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="security-setting-item">
              <div>
                <div className="security-setting-title">Push Notifications</div>
                <div className="security-setting-description">
                  Receive security alerts on your device
                </div>
              </div>
              <label className="tesla-switch">
                <input
                  type="checkbox"
                  checked={securitySettings.pushNotifications}
                  onChange={() => handleToggleSetting('pushNotifications')}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Download Your Data</h2>
          </div>
          <div className="tesla-card__content">
            <p className="security-setting-description" style={{ marginBottom: '1rem' }}>
              Export a copy of your LookBel account data. Re-enter your password to confirm.
            </p>
            <div className="form-group">
              <label htmlFor="exportPassword">Confirm password to export</label>
              <div className="password-input-container">
                <input
                  type={showExportPassword ? 'text' : 'password'}
                  id="exportPassword"
                  className="tesla-input"
                  value={exportPassword}
                  onChange={(e) => setExportPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowExportPassword((prev) => !prev)}
                >
                  {showExportPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <TeslaButton
              variant="primary"
              onClick={handleExportAccountData}
              disabled={loading || !exportPassword.trim()}
            >
              {loading ? 'Exporting...' : 'Download Data Export'}
            </TeslaButton>
          </div>
        </div>

        <div className="tesla-card tesla-card--danger">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Danger Zone</h2>
          </div>
          <div className="tesla-card__content">
            {scheduledDeletionDate ? (
              <>
                <p className="security-setting-description" style={{ marginBottom: '0.75rem' }}>
                  You requested account deletion. Your account has not been deleted, and you
                  can keep using LookBel. You can cancel the request until{' '}
                  <strong>{deletionDateLabel}</strong>.{' '}
                  <Link to="/delete-account">What happens next</Link>
                </p>
                <TeslaButton
                  variant="primary"
                  onClick={handleCancelDeletion}
                  disabled={loading}
                >
                  {loading ? 'Cancelling...' : 'Cancel Account Deletion'}
                </TeslaButton>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="deletePassword">Confirm password to request deletion</label>
                  <div className="password-input-container">
                    <input
                      type={showDeletePassword ? 'text' : 'password'}
                      id="deletePassword"
                      className="tesla-input"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowDeletePassword((prev) => !prev)}
                    >
                      {showDeletePassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                <TeslaButton
                  variant="outline"
                  onClick={handleDeleteAccount}
                  disabled={loading || !deletePassword.trim()}
                  style={{ backgroundColor: '#C0392B', color: '#FFF', borderColor: '#C0392B' }}
                >
                  <DeleteIcon />
                  {loading ? 'Requesting...' : 'Request Account Deletion'}
                </TeslaButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
